import * as _ from 'lodash';
import { Brand, BrandSearchResult, FcProductListItem, FcProductPageRoute, FcProducts, SelectedProduct } from './types';
import { ConnectionStatus } from '../providers/brand-access.service';
import { BrandAccessRight, FcProductConnectionStatus, FcProductSuites, ProductsValues } from '../types';
import { AccessState, Relations } from 'shared/models';
import { AutoRestockBrand } from '../../../smart-replenishment/retailer-platform/smart-replenishment/auto-restock/types';
import { SuggestionBrand } from '../../../smart-replenishment/retailer-platform/smart-replenishment/suggestions/types';
import { brandShopUrlPrefix, retailersUrlPrefix } from '../../brand-shop/utils';
import { FcProductName } from 'types';

export const sortItemsByIndex = (items: any[]) => items.sort((a, b) => a?.index - b?.index);

export const fcProductList: FcProductListItem[] = sortItemsByIndex([
    { product: 'all', index: 0, translationKey: 'TEXT_ALL' },
    {
        index: 5,
        product: FcProducts.Content,
        icon: 'image',
        translationKey: 'CONTENT_PAGE.TITLE',
        defaultAccessRight: BrandAccessRight.ProductData,
        relatedAccessRight: [
            BrandAccessRight.ContentPlatform,
            BrandAccessRight.MarketingMaterial,
            BrandAccessRight.ProductData
        ]
    },
    {
        index: 1,
        product: FcProducts.Reorder,
        icon: 'shopping-cart',
        translationKey: 'ORDER_PLATFORM_PAGE.TITLE',
        defaultAccessRight: BrandAccessRight.ReorderPlatform,
        relatedAccessRight: [BrandAccessRight.ReorderPlatform]
    },
    {
        index: 2,
        product: FcProducts.Preorder,
        icon: 'list-alt',
        translationKey: 'PREORDER_AREA.TITLE',
        defaultAccessRight: BrandAccessRight.PreorderPlatform,
        relatedAccessRight: [BrandAccessRight.PreorderPlatform]
    },
    {
        index: 4,
        product: FcProducts.AutoRestock,
        icon: 'redo',
        translationKey: 'ORDER_AUTO_RESTOCK.TITLE'
    },
    {
        index: 3,
        product: FcProducts.Suggestions,
        icon: 'lightbulb',
        translationKey: 'ORDER_SUGGESTIONS.TITLE'
    }
]);

export const fcProductConnectionStatusMapper = (): Record<ConnectionStatus, FcProductConnectionStatus> => ({
    [ConnectionStatus.Connected]: FcProductConnectionStatus.Connected,
    [ConnectionStatus.Pending]: FcProductConnectionStatus.Pending,
    [ConnectionStatus.Connect]: FcProductConnectionStatus.Unconnected
});

export const fcProductPageRouteMapper = (brandId, suffix?: string): Record<FcProducts, FcProductPageRoute> => ({
    [FcProducts.Content]: {
        urlFragments: [
            retailersUrlPrefix,
            brandShopUrlPrefix,
            brandId,
            FcProductSuites.Content,
            suffix ? suffix : 'marketing'
        ]
    },
    [FcProducts.Preorder]: {
        urlFragments: [retailersUrlPrefix, brandShopUrlPrefix, brandId, FcProductSuites.Preorder, 'search']
    },
    [FcProducts.Reorder]: {
        urlFragments: [retailersUrlPrefix, brandShopUrlPrefix, brandId, FcProductSuites.Reorder, 'search'],
        queryParams: { minimumStock: 1 }
    },
    [FcProducts.AutoRestock]: {
        urlFragments: [retailersUrlPrefix, brandShopUrlPrefix, brandId, FcProductSuites.AutoRestock]
    },
    [FcProducts.Suggestions]: {
        urlFragments: [retailersUrlPrefix, brandShopUrlPrefix, brandId, FcProductSuites.Suggestions]
    }
});

export const mapFcProductToProductFilter = (): Record<FcProducts, ProductsValues> => ({
    [FcProducts.Content]: ['ProductData', 'MarketingMaterial'],
    [FcProducts.Reorder]: ['Reorder'],
    [FcProducts.Preorder]: ['Preorder'],
    [FcProducts.AutoRestock]: ['AutoRestock'],
    [FcProducts.Suggestions]: ['Suggestions']
});

export const fcProductKeyToBrandAccessRightMap = {
    [FcProductName.ContentPlatform]: BrandAccessRight.ContentPlatform,
    [FcProductName.ProductData]: BrandAccessRight.ProductData,
    [FcProductName.MarketingMaterial]: BrandAccessRight.MarketingMaterial,
    [FcProductName.PreorderPlatform]: BrandAccessRight.PreorderPlatform,
    [FcProductName.OrderPlatform]: BrandAccessRight.ReorderPlatform,
    [FcProductName.EndlessAisle]: BrandAccessRight.EndlessAisle
};

export const getAccessRightRelatedFcProduct = (accessRight: BrandAccessRight) =>
    fcProductList.find((product) => product?.relatedAccessRight && product?.relatedAccessRight.includes(accessRight));

export const hasBrandFcProductConnections = <T extends Brand>(brand: BrandSearchResult<T>): boolean =>
    _.some(brand.relations, (status: AccessState) => status === AccessState.Granted);

export const hasBrandPendingFcProductConnections = <T extends Brand>(brand: BrandSearchResult<T>): boolean =>
    !_.some(brand.relations, (status: AccessState) => status === AccessState.Granted) &&
    _.some(brand.relations, (status: AccessState) => status === AccessState.Pending);

export const hasBrandNoFcProductConnections = <T extends Brand>(brand: BrandSearchResult<T>): boolean =>
    _.every(brand.relations, (status: AccessState) => status !== AccessState.Granted && status !== AccessState.Pending);

export const hasBrandGrantedAccessToFcProduct = <T extends Brand>(
    brand: BrandSearchResult<T>,
    product: SelectedProduct
): boolean =>
    (product === FcProducts.AutoRestock && brand.hasAutoRestockConnection) ||
    (product === FcProducts.Suggestions && brand.hasSuggestionConnection) ||
    _.some(
        brand.relations,
        (status: AccessState, accessRight: BrandAccessRight) =>
            status === AccessState.Granted && getAccessRightRelatedFcProduct(accessRight)?.product === product
    );

export const filterBrandRelations = (relations: Brand['relations']) =>
    _.omitBy(relations, (_, rightName) => rightName === BrandAccessRight.EndlessAisle) as Relations;

export const standardizeBrandSearchResults = <T extends Brand>({
    brands,
    autoRestockBrands,
    suggestionBrands
}: {
    brands: T[];
    autoRestockBrands: AutoRestockBrand[];
    suggestionBrands: SuggestionBrand[];
}) => {
    const autoRestockBrandIds = autoRestockBrands.map((brand) => brand._id);
    const suggestionBrandIds = suggestionBrands.map((brand) => brand._id);

    return brands.map((brand) => {
        const result = {
            ..._.omit(brand, 'relations'),
            relations: filterBrandRelations(brand.relations),
            hasAutoRestockConnection: autoRestockBrandIds.includes(brand._id),
            hasSuggestionConnection: suggestionBrandIds.includes(brand._id),
            isAutoRestockEnabled: autoRestockBrandIds.includes(brand._id)
                ? _.get(_.find(autoRestockBrands, { _id: brand._id }), 'enabled', false)
                : false,
            openSuggestionsCount: suggestionBrands.find(({ _id }) => _id === brand._id)?.openSuggestionsCount.toString()
        } as unknown as BrandSearchResult<T>;

        return {
            ...result,
            // Overwritten because autostock and suggestion connections are not taken into account in the original connection status logic.
            connectionStatus:
                hasBrandFcProductConnections(result) ||
                result.hasAutoRestockConnection ||
                result.hasSuggestionConnection
                    ? ConnectionStatus.Connected
                    : hasBrandPendingFcProductConnections(result)
                      ? ConnectionStatus.Pending
                      : ConnectionStatus.Connect
        };
    });
};
